import { useEffect, useMemo, useState } from 'react';
import { loadMaterialCategoriesCached, MaterialCategory, MaterialCategoryNode } from '../api/materialCategory';

// 喷墨等「按类别挂参数」的框架 (material_key_kind = category_code) 的类别选择.
//
// 二级类别编码只在父级下唯一 (如 Plastic/ABS 与 3D Printing Filament/ABS),
// 参数行因此存一级 + 二级两列 (type1 + type2, 与材料 type1/type2 同义, 值为类别编码 categoryCode).
// 下拉选项的 value 把两段编进一个字符串, 选中后再拆回两列; 显示「一级中文 / 二级中文」.
// 三处页面 (参数管理 / 参数列表 / 建设备向导) 共用这一份, 不各自摊平类别树.

/** 一级 / 二级之间的分隔符: 单元分隔符 U+001F, 不会出现在类别编码里. */
const PAIR_SEP = '\u001f';

export interface CategoryPair {
    type1: string;
    type2: string;
}

/** (一级, 二级) → 下拉 value; 一级为空 (存量行未回填) 时只用二级编码. */
export function encodeCategoryPair(type1?: string, type2?: string): string {
    if (!type2) return '';
    return type1 ? `${type1}${PAIR_SEP}${type2}` : type2;
}

/** 下拉 value → (一级, 二级); 无分隔符的旧值视为只有二级. */
export function decodeCategoryPair(value: string): CategoryPair {
    const i = value.indexOf(PAIR_SEP);
    if (i < 0) return { type1: '', type2: value };
    return { type1: value.slice(0, i), type2: value.slice(i + 1) };
}

export interface CategoryPairOption {
    value: string;
    label: string;
}

function nameOf(c: MaterialCategory): string {
    return c.nameZh || c.nameEn;
}

/** 类别树 → 二级类别选项, 值为类别编码对, 显示「一级中文 / 二级中文」. */
function pairOptionsOf(tree: MaterialCategoryNode[]): CategoryPairOption[] {
    return tree.flatMap((l1) =>
        (l1.children || []).map((l2) => ({
            value: encodeCategoryPair(l1.categoryCode, l2.categoryCode),
            label: `${nameOf(l1)} / ${nameOf(l2)}`,
        })),
    );
}

/**
 * 类别对选项 (enabled=false 时不拉类别树, 返回空数组) + 显示名函数.
 * text(一级, 二级): 「一级中文 / 二级中文」; 一级为空的存量行只显示二级 (中文名取不准时回退原值).
 */
export function useCategoryPairOptions(enabled = true): {
    options: CategoryPairOption[];
    text: (type1?: string, type2?: string) => string;
} {
    const [tree, setTree] = useState<MaterialCategoryNode[]>([]);
    useEffect(() => {
        if (!enabled) return;
        let alive = true;
        loadMaterialCategoriesCached().then((t) => {
            if (alive) setTree(t);
        });
        return () => {
            alive = false;
        };
    }, [enabled]);
    const options = useMemo(() => pairOptionsOf(tree), [tree]);
    const labelOf = useMemo(() => new Map(options.map((o) => [o.value, o.label])), [options]);
    const text = (type1?: string, type2?: string): string => {
        if (!type2) return '';
        if (!type1) return type2;
        return labelOf.get(encodeCategoryPair(type1, type2)) || formatCategoryPair(type1, type2);
    };
    return { options, text };
}

/** 不取类别树时的原值显示「一级 / 二级」(类别编码); 一级为空的存量行只显示二级. */
export function formatCategoryPair(type1?: unknown, type2?: unknown): string {
    const t1 = type1 ? String(type1) : '';
    const t2 = type2 ? String(type2) : '';
    if (!t2) return '';
    return t1 ? `${t1} / ${t2}` : t2;
}

/** 存量值 (一级为空 / 类别已删) 不在选项里时的显示名, 配合 searchOptions.withCurrent 补进下拉不丢值. */
export function categoryPairFallbackLabel(value: string): string {
    const { type1, type2 } = decodeCategoryPair(value);
    return type1 ? formatCategoryPair(type1, type2) : `${type2} (未指定一级类别)`;
}
